"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const helper_1 = require("../helper");
const shopping_basket_calculator_1 = require("@touridev/shopping-basket-calculator");
class ShoppingBasket {
    containedItems = [];
    containedDiscounts = [];
    containedShipping;
    storage;
    events = [];
    combineItems = false;
    orderItemsBy = {
        key: "CurrentPrice",
        direction: "desc",
    };
    constructor(options) {
        this.storage = options?.storage;
        if (options?.orderItemsBy) {
            if (typeof options.orderItemsBy === "string") {
                this.orderItemsBy.key = options.orderItemsBy;
            }
            else {
                this.orderItemsBy = options.orderItemsBy;
            }
        }
        if (typeof options?.combineItems === "boolean")
            this.combineItems = options.combineItems;
    }
    on(name, callback) {
        this.events.push({ name, callback });
    }
    off(name, callback) {
        const existingEvent = this.events.find((evt) => evt.name === name && evt.callback === callback);
        if (!existingEvent)
            return;
        this.events = this.events.filter((evt) => !(evt.name === existingEvent.name && evt.callback === existingEvent.callback));
    }
    emit(name, ...args) {
        const events = this.events.filter((evt) => evt.name === name);
        events.forEach((event) => event.callback(...args));
    }
    async load() {
        if (this.storage) {
            const { items, discounts, shipping } = await this.storage.load();
            this.containedItems = items;
            this.containedDiscounts = discounts;
            this.containedShipping = shipping;
        }
    }
    async save() {
        if (!this.storage)
            return;
        return await this.storage.save(this.data);
    }
    get discounts() {
        return this.containedDiscounts;
    }
    get data() {
        return {
            items: this.containedItems,
            discounts: this.containedDiscounts,
            shipping: this.containedShipping,
        };
    }
    get items() {
        return this.containedItems;
    }
    set items(items) {
        this.containedItems = JSON.parse(JSON.stringify(items));
    }
    get uniqueItems() {
        const items = (0, helper_1.getUniqueObjectItems)(this.containedItems).map((item) => {
            return {
                ...item,
                Quantity: this.getItemQuantity(item),
            };
        });
        return items.sort((a, b) => {
            const values = this.orderItemsBy.direction === "asc" ? [a[this.orderItemsBy.key], b[this.orderItemsBy.key]] : [b[this.orderItemsBy.key], a[this.orderItemsBy.key]];
            if ((0, helper_1.isNumeric)(values[0]) && (0, helper_1.isNumeric)(values[1]))
                return values[0] - values[1];
            return values[0].localeCompare(values[1]);
        });
    }
    get overallShippingCost() {
        if (!this.containedShipping?.value)
            return 0;
        let total = 0;
        this.containedItems.forEach(() => {
            total += this.containedShipping.value;
        });
        return total;
    }
    get overallItemCost() {
        return (0, helper_1.sum)(...this.items.map((item) => item.CurrentPrice));
    }
    get overallItemValue() {
        return (0, helper_1.sum)(...this.items.map((item) => item.RegularPrice));
    }
    get overallDiscountedItemCost() {
        const { total } = (0, shopping_basket_calculator_1.useCalculate)(this.items, this.discounts);
        return total;
    }
    get overallCost() {
        return this.overallDiscountedItemCost + this.overallShippingCost;
    }
    get overallDiscountValue() {
        const { discount } = (0, shopping_basket_calculator_1.useCalculate)(this.items, this.discounts);
        return discount;
    }
    get remainingDiscountValue() {
        const { remaining } = (0, shopping_basket_calculator_1.useCalculate)(this.items, this.discounts);
        return remaining;
    }
    get shipping() {
        return this.containedShipping;
    }
    async setShipping(shippingCost) {
        this.containedShipping = shippingCost;
        this.emit("changeshipping", shippingCost);
        await this.save();
    }
    contains(item) {
        return !!this.containedItems.find((itm) => {
            return (0, helper_1.isSameObject)(itm, item);
        });
    }
    getItemQuantity(item) {
        let count = 0;
        if (!this.contains(item))
            return count;
        this.containedItems.forEach((i) => {
            if ((0, helper_1.isSameObject)(i, item)) {
                count++;
            }
        });
        return count;
    }
    async addDiscount(discount) {
        this.containedDiscounts.push(discount);
        this.emit("adddiscount", discount);
        await this.save();
    }
    async setDiscount(index, discount) {
        if (index === -1)
            return;
        this.containedDiscounts.splice(index, 1, discount);
        this.emit("adddiscount", discount);
        await this.save();
    }
    async removeDiscount(discount) {
        const existingDiscount = this.containedDiscounts.find((d) => d.id === discount.id && d.value === discount.value && d.isPercentage === discount.isPercentage);
        if (!existingDiscount)
            return;
        this.containedDiscounts = this.containedDiscounts.filter((d) => d !== existingDiscount);
        this.emit("removediscount", discount);
        await this.save();
    }
    findDiscount(id) {
        return this.containedDiscounts.find((discount) => discount?.id === id);
    }
    getIndexOfItem(item) {
        for (let i = 0; i < this.containedItems.length; i++) {
            if ((0, helper_1.isSameObject)(this.containedItems[i], item))
                return i;
        }
        return -1;
    }
    async changeItem(itemIndex, values) {
        if (itemIndex === -1 || this.containedItems.length <= itemIndex || !this.containedItems[itemIndex])
            return;
        const item = this.containedItems[itemIndex];
        const data = values;
        Object.keys(data).forEach((key) => {
            if (!(key in item))
                return;
            item[key] = data[key];
        });
        this.emit("changeitem", item);
        await this.save();
        return item;
    }
    async changeItemQuantity(item, quantity) {
        item = this.makeItem(item);
        if (!this.contains(item))
            return await this.addItem(item, quantity);
        if (!quantity && this.contains(item))
            return await this.removeItem(item);
        const currentQuantity = this.getItemQuantity(item);
        if (quantity > currentQuantity) {
            return await this.addItem(item, quantity - currentQuantity);
        }
        return await this.removeItem(item, currentQuantity - quantity);
    }
    makeItem = (item) => {
        item.Quantity = undefined;
        const itm = JSON.parse(JSON.stringify(item));
        if (!this.combineItems)
            return itm;
        this.containedItems.forEach((i, index) => {
            if (i.id === itm.id)
                this.containedItems[index] = { ...itm };
        });
        return itm;
    };
    async addItem(item, quantity = 1) {
        for (let i = 0; i < quantity; i++) {
            this.containedItems.push(this.makeItem(item));
        }
        this.emit("additem", {
            ...item,
            Quantity: quantity,
        });
        await this.save();
    }
    remove(item) {
        if (!this.contains(item))
            return false;
        const index = this.getIndexOfItem(item);
        if (index === -1)
            return false;
        this.containedItems.splice(index, 1);
        return true;
    }
    async removeItem(item, quantity = 1) {
        item = this.makeItem(item);
        if (!quantity)
            return;
        for (let q = 0; q < quantity; q++) {
            this.remove(item);
        }
        this.emit("removeitem", {
            ...item,
            Quantity: quantity,
        });
        await this.save();
    }
    async clear() {
        this.containedItems = [];
        this.containedDiscounts = [];
        this.containedShipping = undefined;
        this.emit("clear");
        if (this.storage?.clear)
            await this.storage?.clear();
    }
    async clearItems() {
        this.containedItems = [];
        await this.save();
    }
}
exports.default = ShoppingBasket;
